/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';

const {
  i18n: {
    __,
  },
} = wp;

const LocationAutocomplete = (props) => {
  const { isSearchExpanded, suggestedRegions } = props;
  const [weatherSearchResults, setWeatherSearchResults] = useState({
    results: null,
  });
  const [selectedLocationIndex, setSelectedLocationIndex] = useState(0);
  const setNoOptsMsg = 'telemundo' === nbc.brand ? 'No hay resultados' : 'No Results'; // eslint-disable-line max-len
  const locale = 'telemundo' === nbc.brand ? '&language=es-US' : '&language=en-US'; // eslint-disable-line max-len

  /**
   * Process the JSON.
   *
   * @param {String} json
   * @returns {Array}
   */
  const processJSON = (json) => {
    const {
      location = {},
    } = json;

    const {
      city: cities = [],
    } = location;

    return cities.map((city, index) => {
      const {
        adminDistrictCode: {
          [index]: state = '',
        } = {},
        countryCode: {
          [index]: country = '',
        } = {},
        displayName: {
          [index]: displayName = '',
        } = {},
        postalCode: {
          [index]: zipCode = '',
        } = {},
      } = location;

      let label = `${city || displayName || ''}`;

      if (zipCode) {
        label += ` (${zipCode})`;
      }

      label += `, ${state || country}`;

      if ('Grapevine (75261), TX' === label) {
        label = 'DFW Airport (75261), TX';
      }

      return {
        value: `${zipCode}|${index}`,
        label: `${label}`,
      };
    });
  };

  /**
   * Fetch data from weather API.
   *
   * @param {String} inputValue Search query.
   * @returns {Promise<Array | never>}
   */
  const getResults = (inputValue) => fetch(`https://api.weather.com/v3/location/search?query=${inputValue}${locale}&format=json&countryCode=US&apiKey=c8abfe32a8c14073abfe32a8c120731d`)
    .then((response) => {
      if (! response.ok) {
        setWeatherSearchResults({ results: null });
        throw response;
      }
      return response;
    })
    .then((response) => response.json())
    .then((json) => {
      const filteredJson = (() => {
        // filters out non-US results.
        const {
          location: {
            countryCode: countryCodes,
          },
        } = json;

        const found = [];
        const allowList = ['US', 'PR'];

        countryCodes.forEach((_str, index) => {
          if (! allowList.includes(_str)) {
            found.push(index);
          }
        });

        const newLocation = {};

        Object.keys(json.location).forEach(($key) => {
          newLocation[$key] = json
            .location[$key]
            .filter((obj, index) => ! found.includes(index));
        });

        return {
          location: newLocation,
        };
      })();
      setWeatherSearchResults({ results: filteredJson });
    });

  /**
   * Handle location change.
   *
   * @param {String} option Value object.
   */
  const handleOnChange = (option) => {
    const {
      value = '',
    } = option;

    // search results provide index, suggested regions do not.
    const splitValue = value.split('|');

    const index = 2 === splitValue.length ? splitValue[1] : - 1;

    if (- 1 !== index) {
      const {
        results: {
          location: {
            postalCode: {
              [index]: zipCode = '',
            } = {},
            latitude: {
              [index]: lat = '',
            } = {},
            longitude: {
              [index]: lng = '',
            } = {},
          } = {},
        } = {},
      } = weatherSearchResults;

      localStorage.setItem('nbc_weather_coords', JSON.stringify({ lat, lng }));
      localStorage.setItem('zipCode', zipCode);
    } else {
      localStorage.setItem('nbc_weather_coords', JSON.stringify({}));
      localStorage.setItem('zipCode', value);
    }

    // reload the window.
    if (
      'undefined' !== typeof window
      && window.location // phpcs:ignore WordPressVIPMinimum.JS.Window.location
      && window.location.reload
    ) {
      window.location.reload();
    }
  };

  const { results } = weatherSearchResults;
  const locations = results ? processJSON(results) : null;
  const ref = React.createRef();

  useEffect(() => {
    if (ref.current && isSearchExpanded) {
      ref.current.focus();
    }
  }, []);

  return (
    <Fragment>
      <input
        ref={ref}
        type="text"
        placeholder={__('Search city, zip…', 'nbc')}
        onChange={(e) => {
          getResults(e.target.value);
        }}
        onKeyPress={(e) => {
          if ('Enter' === e.key) {
            e.stopPropagation();
            const { value } = locations[selectedLocationIndex];
            handleOnChange({ value });
          }
        }}
      />
      <div className="location-search__results">
        {
          locations ?
            <Fragment>
              {
                locations.map(({ label, value }, index) => {
                  // eslint-disable-next-line max-len
                  const selectedClass = index === selectedLocationIndex ? 'selected' : '';
                  return (
                    <div
                      className={`location-search__location ${selectedClass}`}
                      onClick={() => handleOnChange({ value })}
                      onMouseOver={() => {
                        setSelectedLocationIndex(index);
                      }}
                      onFocus={() => {
                        setSelectedLocationIndex(index);
                      }}
                      role="button"
                      key={value}
                      tabIndex={index}
                    >
                      {label}
                    </div>
                  );
                })
              }
            </Fragment>
            :
            [
              suggestedRegions.length ?
                <Fragment>
                  <header className="location-search__suggested-regions-header">
                    {__('Suggested Regions', 'nbc')}
                  </header>
                  {
                    suggestedRegions.map(({ name, value }, index) => (
                      <div
                        className="location-search__suggested-region"
                        onClick={() => handleOnChange({ value })}
                        role="button"
                        key={value}
                        tabIndex={index}
                      >
                        {name}
                      </div>
                    ))
                  }
                </Fragment>
                :
                <div className="location-search__no-results">
                  {setNoOptsMsg}
                </div>,
            ]
        }
      </div>
    </Fragment>
  );
};

LocationAutocomplete.propTypes = {
  isSearchExpanded: PropTypes.bool.isRequired,
  suggestedRegions: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.string,
  })).isRequired,
};

export default LocationAutocomplete;
